/**
 * Branded SVG glyphs for connector platforms.
 *
 * Source attribution:
 * - Glyphs for brands currently in simple-icons v16 are pulled from the
 *   package directly via named imports.
 * - Brands no longer carried by simple-icons v16 (LinkedIn, Amazon, Bing,
 *   Yahoo) are inlined verbatim from earlier simple-icons releases.
 *   All are CC0-licensed; source tags are noted on each entry.
 *
 * Connector types missing from this map fall through to the colored-tile
 * + first-letter rendering in `platformLogos.ts`.
 *
 * Adding a new platform:
 *   1. Verify the slug exists in simple-icons v16 by checking node_modules.
 *   2. Add the named import and the PLATFORM_GLYPHS entry.
 *   3. If the brand color is illegible at 18px on a light row background,
 *      add an entry to LOW_CONTRAST_OVERRIDE (see plan task 6).
 */
// siX is the X (formerly Twitter) glyph from simple-icons.
import {
  siGoogleads,
  siGoogleanalytics,
  siGooglesheets,
  siHubspot,
  siReddit,
  siTiktok,
  siX,
  siSnapchat,
} from 'simple-icons';

export type PlatformGlyph = {
  path: string;   // SVG path data, 24x24 viewBox
  hex: string;    // brand hex without leading '#'
};

// LinkedIn — simple-icons v11.14.0 (CC0)
// https://raw.githubusercontent.com/simple-icons/simple-icons/11.14.0/icons/linkedin.svg
const LINKEDIN_GLYPH: PlatformGlyph = {
  path: 'M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z',
  hex: '0A66C2',
};

// Amazon — simple-icons v11.14.0 (CC0)
// https://raw.githubusercontent.com/simple-icons/simple-icons/11.14.0/icons/amazon.svg
const AMAZON_GLYPH: PlatformGlyph = {
  path: 'M.045 18.02c.072-.116.187-.124.348-.022 3.636 2.11 7.594 3.166 11.87 3.166 2.852 0 5.668-.533 8.447-1.595l.315-.14c.138-.06.234-.1.293-.13.226-.088.39-.046.525.13.12.174.09.336-.12.48-.256.19-.6.41-1.006.654-1.244.743-2.64 1.316-4.185 1.726a17.617 17.617 0 01-10.951-.577 17.88 17.88 0 01-5.43-3.35c-.1-.074-.151-.15-.151-.22 0-.047.021-.09.051-.13zm6.565-6.218c0-1.005.247-1.863.743-2.577.495-.71 1.17-1.25 2.04-1.615.796-.335 1.756-.575 2.912-.72.39-.046 1.033-.103 1.92-.174v-.37c0-.93-.105-1.558-.3-1.875-.302-.43-.78-.65-1.44-.65h-.182c-.48.046-.896.196-1.246.46-.35.27-.575.63-.675 1.096-.06.3-.206.465-.435.51l-2.52-.315c-.248-.06-.372-.18-.372-.39 0-.046.007-.09.022-.15.247-1.29.855-2.25 1.82-2.88.976-.616 2.1-.975 3.39-1.05h.54c1.65 0 2.957.434 3.888 1.29.135.15.27.3.405.48.12.165.224.314.283.45.075.134.15.33.195.57.06.254.105.42.135.51.03.104.062.3.076.615.01.313.02.493.02.553v5.28c0 .376.06.72.165 1.036.105.313.21.54.315.674l.51.674c.09.136.136.256.136.36 0 .12-.06.226-.18.314-1.2 1.05-1.86 1.62-1.963 1.71-.165.135-.375.15-.63.045a6.062 6.062 0 01-.526-.496l-.31-.347a9.391 9.391 0 01-.317-.42l-.3-.435c-.81.886-1.603 1.44-2.4 1.665-.494.15-1.093.227-1.83.227-1.11 0-2.04-.343-2.76-1.034-.72-.69-1.08-1.665-1.08-2.94l-.05-.076zm3.753-.438c0 .566.14 1.02.425 1.364.285.34.675.512 1.155.512.045 0 .106-.007.195-.02.09-.016.134-.023.166-.023.614-.16 1.08-.553 1.424-1.178.165-.28.285-.58.36-.91.09-.32.12-.59.135-.8.015-.195.015-.54.015-1.005v-.54c-.84 0-1.484.06-1.92.18-1.275.36-1.92 1.17-1.92 2.43l-.035-.02zm9.162 7.027c.03-.06.075-.11.132-.17.362-.243.714-.41 1.05-.5a8.094 8.094 0 011.612-.24c.14-.012.28 0 .41.03.65.06 1.05.168 1.172.33.063.09.099.228.099.39v.15c0 .51-.149 1.11-.424 1.8-.278.69-.664 1.248-1.156 1.68-.073.06-.14.09-.197.09-.03 0-.06 0-.09-.012-.09-.044-.107-.12-.064-.24.54-1.26.806-2.143.806-2.64 0-.15-.03-.27-.087-.344-.145-.166-.55-.257-1.224-.257-.243 0-.533.016-.87.046-.363.045-.7.09-1 .135-.09 0-.148-.014-.18-.044-.03-.03-.036-.047-.02-.077 0-.017.006-.03.02-.063v-.06z',
  hex: 'FF9900',
};

// Microsoft Bing — simple-icons v11.14.0 (CC0)
// https://raw.githubusercontent.com/simple-icons/simple-icons/11.14.0/icons/microsoftbing.svg
const BING_GLYPH: PlatformGlyph = {
  path: 'M20.176 15.406a6.48 6.48 0 01-1.736 4.414c1.338-1.47.803-3.869-1.003-4.635-.862-.305-2.488-.85-3.367-1.158a1.834 1.834 0 01-.932-.818c-.381-.975-1.163-2.968-1.548-3.948-.095-.285-.31-.625-.265-.938.046-.598.724-1.003 1.276-.754l3.682 1.888c.621.292 1.305.692 1.796 1.172a6.486 6.486 0 012.097 4.777zm-1.44 1.888c-.264-1.194-1.135-1.744-2.216-2.028-1.527.902-4.853 2.878-6.952 4.13-1.103.68-2.13 1.35-2.919 1.242a2.866 2.866 0 01-2.77-2.325c-.012-.048-.008-.03-.001.01a6.4 6.4 0 00.947 2.653 6.498 6.498 0 005.486 3.022c1.908.062 3.536-1.153 5.099-2.096.292-.188.804-.496 1.332-.831l1.423-1.51c.553-.577.764-1.426.571-2.267zm-12.04 2.97c.422 0 .822-.1 1.173-.29.355-.215.964-.579 1.7-1.018L9.57 4.502c0-.99-.497-1.864-1.257-2.382-.08-.059-2.91-1.901-2.99-1.956-.605-.432-1.523.045-1.5.797v14.887l.417 2.36a2.488 2.488 0 002.455 2.056z',
  hex: '008373',
};

// Yahoo — simple-icons v8.15.0 (CC0)
// https://raw.githubusercontent.com/simple-icons/simple-icons/8.15.0/icons/yahoo.svg
const YAHOO_GLYPH: PlatformGlyph = {
  path: 'M18.86 1.56L14.27 11.87H19.4L24 1.56H18.86M0 6.71L5.15 18.27L3.3 22.44H7.83L14.69 6.71H10.19L7.39 13.44L4.62 6.71H0M15.62 12.87C13.95 12.87 12.71 14.12 12.71 15.58C12.71 17 13.91 18.19 15.5 18.19C17.18 18.19 18.43 16.96 18.43 15.5C18.43 14.03 17.23 12.87 15.62 12.87Z',
  hex: '6001A1',
};

// Platforms absent from this map fall through to the colored-tile letter
// rendering in `platformLogos.ts` (e.g., beeswax, basis_ads).
export const PLATFORM_GLYPHS = {
  google_ads:        { path: siGoogleads.path,       hex: siGoogleads.hex       },
  google_analytics:  { path: siGoogleanalytics.path, hex: siGoogleanalytics.hex },
  google_sheets:     { path: siGooglesheets.path,    hex: siGooglesheets.hex    },
  bing_ads:          BING_GLYPH,
  linkedin_ads:      LINKEDIN_GLYPH,
  hubspot:           { path: siHubspot.path,         hex: siHubspot.hex         },
  reddit_ads:        { path: siReddit.path,          hex: siReddit.hex          },
  tiktok_ads:        { path: siTiktok.path,          hex: siTiktok.hex          },
  twitter_ads:       { path: siX.path,               hex: siX.hex               },
  snapchat_ads:      { path: siSnapchat.path,        hex: siSnapchat.hex        },
  amazon_dsp:        AMAZON_GLYPH,
  yahoo_dsp:         YAHOO_GLYPH,
} satisfies Record<string, PlatformGlyph>;

/**
 * Brand-hex overrides for marks that fail the contrast bar at 18px on a
 * light row background. Hand-curated by visual QA — see plan task 6.
 */
export const LOW_CONTRAST_OVERRIDE: Partial<Record<keyof typeof PLATFORM_GLYPHS, string>> = {
  snapchat_ads: '000000',  // canonical yellow ghost is invisible on white
};
